import { useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useNavigate } from 'react-router';

import { useCreateOrderMutation } from '~domains/orders/entities/order';
import {
  isProduct,
  useFindProductByParams,
} from '~domains/orders/entities/product';

import type { Pos } from '~entities/pos';
import { isOnlinePos, useGetPointsOfSalesQuery } from '~entities/pos';

import PlusIcon from '~shared/assets/icons/plus.svg?react';
import { ROUTES } from '~shared/config/routes';
import { Footer } from '~shared/ui/footer';
import { InlineAlert } from '~shared/ui/inline-alert';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { Button } from '@breezeventures/ui-kit/button';
import { Modal } from '@breezeventures/ui-kit/modal';
import { PhoneField } from '@breezeventures/ui-kit/phone-field/rhf';
import { Select } from '@breezeventures/ui-kit/select/rhf';
import { TextField } from '@breezeventures/ui-kit/text-field/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';
import {
  combineValidators,
  validators,
} from '@breezeventures/web-ph-validators';

import { ItemForm } from './item-form/item-form';
import { OrderItems } from './order-items/order-items';
import { OrderLink } from './order-link/order-link';

import { convertFormValuesToOrder } from '../lib/convert-form-values-to-order';
import { getTotalPrice } from '../lib/get-total-price';
import { validateLoanAmount } from '../lib/loan-validator';

export type ItemFormValue = {
  category: string;
  name: string;
  price: string;
  quantity: number | string;
  isUsed?: boolean;
};

export type OrderFormValues = {
  posId: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  orderNumber?: string;
  items: ItemFormValue[];
};

export const OrderForm = () => {
  const methods = useForm<OrderFormValues>({
    mode: 'all',
    defaultValues: {
      posId: '',
      phone: '',
      firstName: '',
      lastName: '',
      email: '',
      orderNumber: '',
      items: [],
    },
  });
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;
  const fieldArrayMethods = useFieldArray({
    control,
    name: 'items',
  });
  const { fields, append } = fieldArrayMethods;
  const [itemPopupOpened, setItemPopupOpened] = useState<boolean>(false);
  const [linkPopupOpened, setLinkPopupOpened] = useState<boolean>(false);
  const navigate = useNavigate();

  const {
    data: posList = [],
    isSuccess,
    isLoading: isPosListLoading,
  } = useGetPointsOfSalesQuery({ withInactive: false });

  const onlinePosList = useMemo(() => posList.filter(isOnlinePos), [posList]);
  const onlinePosListMap = useMemo(
    () =>
      onlinePosList.reduce(
        (result, pos) => {
          result[pos.id] = pos;
          return result;
        },
        {} as Record<string, Pos>
      ),
    [onlinePosList]
  );
  const posTitleMap = useMemo(
    () => Object.fromEntries(onlinePosList.map((pos) => [pos.id, pos.name])),
    [onlinePosList]
  );

  useEffect(() => {
    if (isSuccess && onlinePosList.length === 1) {
      setValue('posId', onlinePosList[0].id);
    }
  }, [onlinePosList, isSuccess, setValue]);

  const posId = watch('posId');
  const merchantId = onlinePosListMap[posId]?.merchantId;
  const orderItems = watch('items')?.map((field) => ({
    ...field,
    price: +field.price,
    quantity: +field.quantity,
  }));

  const { data: findProductResult, isFetching: isProductLoading } =
    useFindProductByParams(
      {
        pointOfSaleId: posId,
        merchantId,
        orderItems,
      },
      { enabled: !!posId && !!merchantId && orderItems?.length > 0 }
    );

  const isProductFound = isProduct(findProductResult);

  const {
    mutate,
    isPending: isOrderCreating,
    isSuccess: isOrderCreated,
    data: createOrderResponse,
    error: createOrderError,
  } = useCreateOrderMutation();

  const totalPrice = useMemo(() => getTotalPrice(fields), [fields]);
  const totalPriceError =
    totalPrice > 0 &&
    findProductResult &&
    validateLoanAmount(totalPrice, findProductResult);

  const itemsError = errors.items?.message;

  const submitForm = (values: OrderFormValues) => {
    if (!values.items.length) {
      setError('items', {
        type: 'min',
        message: 'Add at least one item to create the order',
      });
      return;
    }

    if (!isProductFound || isOrderCreating || totalPriceError) {
      return;
    }

    clearErrors('orderNumber');

    mutate(convertFormValuesToOrder(values, findProductResult.productId));
  };

  useEffect(() => {
    if (
      createOrderError &&
      typeof createOrderError === 'object' &&
      'code' in createOrderError
    ) {
      if (createOrderError.code === 'ORDER_NUMBER_ALREADY_EXISTS') {
        setError('orderNumber', {
          type: 'manual',
          message:
            'This order number already exists. Please enter a unique value',
        });
      }
    }
  }, [createOrderError, setError]);

  useEffect(() => {
    if (isOrderCreated && createOrderResponse.orderFormUrl) {
      setLinkPopupOpened(true);
    }
  }, [isOrderCreated, createOrderResponse]);

  return (
    <>
      <form
        onSubmit={handleSubmit(submitForm)}
        className="flex h-full flex-col"
      >
        <Screen
          header={<ScreenHeader onClose={() => navigate(ROUTES.orders.root)} />}
          footer={
            <Footer
              submitButtonText="Create order"
              isLoading={isOrderCreating || isProductLoading}
            />
          }
        >
          <Typography.Title size="s" tag="h1" className="py-6">
            Create a new order
          </Typography.Title>

          <div className="flex flex-col gap-3">
            <Controller
              name="posId"
              control={control}
              rules={{ required: 'Please, select a store' }}
              render={(props) => (
                <Select
                  {...props}
                  label="Store name"
                  labelPosition="inner"
                  disabled={isPosListLoading || onlinePosList.length === 1}
                  renderValue={(value) => posTitleMap[value] ?? value}
                >
                  {onlinePosList.map((pos) => (
                    <Select.Option key={pos.id} value={pos.id}>
                      <Typography.Text>{pos.name}</Typography.Text>
                      <Select.OptionIndicator />
                    </Select.Option>
                  ))}
                </Select>
              )}
            />
            <Controller
              name="phone"
              control={control}
              rules={{
                validate: combineValidators(
                  validators.phoneLength(),
                  validators.mobilePhoneFormatPh()
                ),
              }}
              render={(props) => (
                <PhoneField
                  {...props}
                  label="Customer’s phone number"
                  labelPosition="inner"
                  inputMode="tel"
                />
              )}
            />
            <Controller
              name="firstName"
              control={control}
              rules={{
                validate: combineValidators(
                  validators.maxLength({ maxLength: 300 }),
                  validators.filipinoName()
                ),
              }}
              render={(props) => (
                <TextField
                  {...props}
                  label="Customer’s first name"
                  labelPosition="inner"
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{
                validate: combineValidators(
                  validators.maxLength({ maxLength: 300 }),
                  validators.filipinoName()
                ),
              }}
              render={(props) => (
                <TextField
                  {...props}
                  label="Customer’s last name"
                  labelPosition="inner"
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              rules={{
                validate: validators.email(),
              }}
              render={(props) => (
                <TextField
                  {...props}
                  label="Customer’s e-mail"
                  labelPosition="inner"
                  inputMode="email"
                />
              )}
            />
            <Controller
              name="orderNumber"
              control={control}
              render={(props) => (
                <TextField
                  {...props}
                  label="Order number"
                  labelPosition="inner"
                />
              )}
            />
          </div>

          <div className="mt-7 mb-5 flex flex-col gap-3">
            <Typography.Text size="m" weight="medium">
              Items to buy
            </Typography.Text>

            <OrderItems {...fieldArrayMethods} />

            {!!itemsError && (
              <InlineAlert type="error" showIcon={false}>
                {itemsError}
              </InlineAlert>
            )}

            {!!totalPriceError && (
              <InlineAlert type="warning" showIcon={false}>
                {totalPriceError}
              </InlineAlert>
            )}

            <Button
              type="button"
              variant="secondary"
              size="l"
              width="full"
              onClick={() => setItemPopupOpened(true)}
            >
              <PlusIcon className="size-5" />
              Add an item
            </Button>
          </div>
        </Screen>
      </form>

      <Modal
        opened={itemPopupOpened}
        onClose={() => setItemPopupOpened(false)}
        title="Item details"
      >
        <FormProvider {...methods}>
          <ItemForm
            onDone={(item) => {
              append(item);
              clearErrors('items');
              setItemPopupOpened(false);
            }}
            allowsUsedItems={onlinePosListMap[posId]?.allowsUsedItems}
          />
        </FormProvider>
      </Modal>

      {createOrderResponse && (
        <Modal
          opened={linkPopupOpened}
          onClose={() => navigate(ROUTES.orders.root)}
          title="Order added!"
          description="Send the link to the customer to continue the application"
        >
          <OrderLink link={createOrderResponse.orderFormUrl} />
        </Modal>
      )}
    </>
  );
};
